import React, { useState, useRef, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
  LayoutDashboard,
  ChefHat,
  UtensilsCrossed,
  Users,
  ShoppingCart,
  BarChart3,
  LogOut,
  Menu,
  X,
  ChevronDown,
  User,
  Settings,
  PlusCircle,
  Building2,
  UserCog,
  Shield,
  Store,
  Check,
  Bell,
  BellRing,
  CheckCircle,
  XCircle,
  ExternalLink,
} from 'lucide-react';
import { clsx } from 'clsx';
import { ThemeSelector } from './ThemeSelector';
import EngiSpiderIcon from '../assets/EngiSpider_Icon.png';
import notificationSound from '../assets/sound.wav';
import { getPageAccessConfig, getUserRolesFromToken, canAccessPage, getActiveRestaurantId, setActiveRestaurantId, getActiveRole, setActiveRole } from '../utils/roleAccess';
import { organizationService } from '../services/organization';
import { OrganizationRestaurant } from '../types';
import { orderRequestService, OrderRequestNotification, WaiterCallNotification } from '../services/orderRequest';
import { webFcmService } from '../services/fcmService';


type PopupNotif =
  | { type: 'order'; data: OrderRequestNotification }
  | { type: 'waiter'; data: WaiterCallNotification };

interface NavigationItem {
  name: string;
  href: string;
  pageKey: string;
  icon: React.ComponentType<{ className?: string }>;
  highlight?: boolean;
}

const ALL_NAV: NavigationItem[] = [
  { name: '🛎️ Place Order',          href: '/place-order',             pageKey: 'place-order',             icon: PlusCircle,    highlight: true },
  { name: 'Dashboard',               href: '/',                         pageKey: 'dashboard',               icon: LayoutDashboard },
  { name: 'Orders',                  href: '/orders',                   pageKey: 'orders',                  icon: ShoppingCart },
  { name: 'Tables',                  href: '/tables',                   pageKey: 'tables',                  icon: Users },
  { name: 'Customers',               href: '/customers',                pageKey: 'customers',               icon: Users },
  { name: 'Menu Items',              href: '/items',                    pageKey: 'items',                   icon: UtensilsCrossed },
  { name: 'Kitchens',                href: '/kitchens',                 pageKey: 'kitchens',                icon: ChefHat },
  { name: 'Reports',                 href: '/reports',                  pageKey: 'reports',                 icon: BarChart3 },
  { name: 'Organization Onboarding', href: '/organization-onboarding',  pageKey: 'organization-onboarding', icon: Building2 },
  { name: 'Employee Management',     href: '/employee-management',      pageKey: 'employee-management',     icon: UserCog },
  { name: 'Role Page Config',        href: '/role-page-config',         pageKey: 'role-page-config',        icon: Shield },
];

export const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [restaurantSelectorOpen, setRestaurantSelectorOpen] = useState(false);
  const [roleSelectorOpen, setRoleSelectorOpen] = useState(false);
  const [activeRole, setActiveRoleState] = useState<string | null>(() => getActiveRole());
  const [restaurants, setRestaurants] = useState<OrganizationRestaurant[]>([]);
  const [activeRestaurantId, setActiveRestaurantIdState] = useState<string | null>(
    () => getActiveRestaurantId()
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const prevRequestIdsRef = useRef<Set<string>>(new Set());
  const prevCallIdsRef = useRef<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [pendingRequests, setPendingRequests] = useState<OrderRequestNotification[]>([]);
  const [waiterCalls, setWaiterCalls] = useState<WaiterCallNotification[]>([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Popup modal queue (one at a time, like mobile)
  const [notifQueue, setNotifQueue] = useState<PopupNotif[]>([]);
  const [currentNotif, setCurrentNotif] = useState<PopupNotif | null>(null);

  // Fetch restaurants for employee users
  useEffect(() => {
    if (user?.organizationId && user?.restaurantRoles?.length) {
      organizationService.getRestaurantsByOrganization(user.organizationId)
        .then(all => {
          const assigned = all.filter(r =>
            user.restaurantRoles!.some(rr => rr.restaurantId === r.id)
          );
          setRestaurants(assigned);
          if (!activeRestaurantId && assigned.length > 0) {
            const firstId = assigned[0].id;
            setActiveRestaurantIdState(firstId);
            setActiveRestaurantId(firstId);
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationId]);

  // Fetch restaurants for admin users (have organizationCode but no organizationId)
  useEffect(() => {
    if (user?.organizationCode && !user?.organizationId) {
      organizationService.getOrganizationByCode(user.organizationCode)
        .then(org => organizationService.getRestaurantsByOrganization(org.id))
        .then(all => {
          setRestaurants(all);
          if (!activeRestaurantId && all.length > 0) {
            const firstId = all[0].id;
            setActiveRestaurantIdState(firstId);
            setActiveRestaurantId(firstId);
          }
        })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.organizationCode]);

  const handleRestaurantChange = (restaurantId: string) => {
    setActiveRestaurantIdState(restaurantId);
    setActiveRestaurantId(restaurantId);
    // Reset role when restaurant changes
    setActiveRoleState(null);
    setActiveRole(null);
    setRoleSelectorOpen(false);
    setRestaurantSelectorOpen(false);
    setProfileDropdownOpen(false);
    navigate('/');
  };

  const handleRoleChange = (role: string) => {
    setActiveRoleState(role);
    setActiveRole(role);
    setRoleSelectorOpen(false);
  };

  // Derive effective roles: active role > restaurant roles > JWT roles
  let effectiveRoles: string[];
  if (user?.restaurantRoles?.length && activeRestaurantId) {
    const entry = user.restaurantRoles.find(rr => rr.restaurantId === activeRestaurantId);
    const restaurantRoles = entry ? (entry.roles as string[]) : getUserRolesFromToken();
    // If a specific role is selected and it's valid for this restaurant, use only that
    effectiveRoles = (activeRole && restaurantRoles.includes(activeRole))
      ? [activeRole]
      : restaurantRoles;
  } else {
    effectiveRoles = getUserRolesFromToken();
  }

  // Roles available for the active restaurant (for the dropdown)
  const activeRestaurantRoles: string[] = (() => {
    if (!user?.restaurantRoles?.length || !activeRestaurantId) return [];
    const entry = user.restaurantRoles.find(rr => rr.restaurantId === activeRestaurantId);
    return entry ? (entry.roles as string[]) : [];
  })();

  const pageConfig = getPageAccessConfig();
  const navigation = ALL_NAV.filter(item => canAccessPage(item.pageKey, effectiveRoles, pageConfig));

  const handleLogout = () => {
    webFcmService.removeToken();
    logout();
    navigate('/login');
    setProfileDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setProfileDropdownOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Start looping sound when popup appears, stop when dismissed
  useEffect(() => {
    if (currentNotif) {
      try {
        const audio = new Audio(notificationSound);
        audio.loop = true;
        audio.play().catch(() => {});
        audioRef.current = audio;
      } catch {}
    } else {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
    }
  }, [currentNotif]);

  const fetchNotifications = useCallback(async () => {
    if (!activeRestaurantId) return;
    try {
      const [requests, calls] = await Promise.all([
        orderRequestService.getPendingRequests(activeRestaurantId),
        orderRequestService.getPendingWaiterCalls(activeRestaurantId),
      ]);
      const newOrders = requests.filter(r => !prevRequestIdsRef.current.has(r.id));
      const newWaiters = calls.filter(c => !prevCallIdsRef.current.has(c.id));
      if (newOrders.length > 0 || newWaiters.length > 0) {
        const incoming: PopupNotif[] = [
          ...newOrders.map(o => ({ type: 'order' as const, data: o })),
          ...newWaiters.map(w => ({ type: 'waiter' as const, data: w })),
        ];
        setNotifQueue(prev => [...prev, ...incoming]);
      }
      prevRequestIdsRef.current = new Set(requests.map(r => r.id));
      prevCallIdsRef.current = new Set(calls.map(c => c.id));
      setPendingRequests(requests);
      setWaiterCalls(calls);
    } catch {}
  }, [activeRestaurantId]);

  // Initial load when restaurantId is available — FCM handles subsequent updates
  useEffect(() => {
    fetchNotifications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRestaurantId]);

  // Initialize FCM — run on login AND on every mount if token is missing
  useEffect(() => {
    if (!user) return;
    webFcmService.init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    // Re-register if token was never stored (e.g. user was already logged in when FCM was set up)
    if (!user) return;
    if (!localStorage.getItem('fcmToken')) {
      webFcmService.init();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // FCM foreground message handler
  useEffect(() => {
    if (!user) return;
    let unsubscribe: (() => void) | undefined;
    webFcmService.onForegroundMessage((_title, _body, data) => {
      const type = data?.type;
      if (type === 'ORDER_REQUEST' || type === 'WAITER_CALL') {
        // New notification — fetch and enqueue popup
        fetchNotifications();
      } else if (type === 'ORDER_REQUEST_HANDLED') {
        // Another device accepted/declined — dismiss if this popup is showing
        const handledId = data?.id;
        if (handledId) {
          prevRequestIdsRef.current.add(handledId);
          setCurrentNotif(prev => (prev?.type === 'order' && prev.data.id === handledId ? null : prev));
          setNotifQueue(prev => prev.filter(n => !(n.type === 'order' && n.data.id === handledId)));
          fetchNotifications();
        }
      } else if (type === 'WAITER_CALL_HANDLED') {
        // Another device acknowledged — dismiss if this popup is showing
        const handledId = data?.id;
        if (handledId) {
          prevCallIdsRef.current.add(handledId);
          setCurrentNotif(prev => (prev?.type === 'waiter' && prev.data.id === handledId ? null : prev));
          setNotifQueue(prev => prev.filter(n => !(n.type === 'waiter' && n.data.id === handledId)));
          fetchNotifications();
        }
      } else {
        // Promotional / broadcast notification — show as toast
        toast(_title + (_body ? '\n' + _body : ''), { duration: 6000, icon: '🔔' });
      }
    }).then(unsub => { unsubscribe = unsub; });
    return () => { unsubscribe?.(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!toastMessage) return;
    const t = setTimeout(() => setToastMessage(null), 4000);
    return () => clearTimeout(t);
  }, [toastMessage]);

  // Dequeue: show next popup when current is dismissed
  useEffect(() => {
    if (!currentNotif && notifQueue.length > 0) {
      setCurrentNotif(notifQueue[0]);
      setNotifQueue(prev => prev.slice(1));
    }
  }, [currentNotif, notifQueue]);

  const dismissPopup = () => {
    setCurrentNotif(null);
    navigate('/notifications');
  };

  const handlePopupAccept = async () => {
    if (!currentNotif || currentNotif.type !== 'order') return;
    try { await orderRequestService.acceptRequest(currentNotif.data.id); } catch {}
    dismissPopup();
    fetchNotifications();
  };

  const handlePopupDecline = async () => {
    if (!currentNotif || currentNotif.type !== 'order') return;
    try { await orderRequestService.rejectRequest(currentNotif.data.id); } catch {}
    dismissPopup();
    fetchNotifications();
  };

  const handlePopupAcknowledge = async () => {
    if (!currentNotif || currentNotif.type !== 'waiter') return;
    try { await orderRequestService.acknowledgeWaiterCall(currentNotif.data.id); } catch {}
    dismissPopup();
    fetchNotifications();
  };

  const handleAcceptRequest = async (id: string) => {
    try {
      await orderRequestService.acceptRequest(id);
      fetchNotifications();
    } catch {}
  };

  const handleRejectRequest = async (id: string) => {
    try {
      await orderRequestService.rejectRequest(id);
      fetchNotifications();
    } catch {}
  };

  const handleAcknowledgeCall = async (id: string) => {
    try {
      await orderRequestService.acknowledgeWaiterCall(id);
      fetchNotifications();
    } catch {}
  };

  const totalNotifications = pendingRequests.length + waiterCalls.length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: currentTheme.colors.background.primary }}>
      {/* Mobile sidebar */}
      <div className={clsx(
        'fixed inset-0 z-50 lg:hidden',
        sidebarOpen ? 'block' : 'hidden'
      )}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className={clsx(
          "fixed inset-y-0 left-0 flex w-64 flex-col shadow-xl",
          currentTheme.styles.sidebar.background,
          currentTheme.styles.sidebar.border
        )}>
          <div className={clsx(
            "flex items-center justify-between h-16 px-4 border-b",
            currentTheme.styles.sidebar.border
          )}>
            <div className="flex items-center space-x-2">
              <img src={EngiSpiderIcon} alt="EngiSpider" className="h-8 w-8" />
              <span className="text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>Restro360</span>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center px-3 py-2 rounded-lg transition-all',
                    (item.highlight ?? false)
                      ? 'text-base font-bold shadow-lg'
                      : 'text-sm font-medium',
                    isActive
                      ? `text-primary-700`
                      : 'hover:bg-opacity-10'
                  )
                }
                style={({ isActive }) => ({
                  backgroundColor: isActive
                    ? currentTheme.colors.primary[100]
                    : (item.highlight ?? false)
                      ? currentTheme.colors.primary[100]
                      : 'transparent',
                  color: isActive
                    ? currentTheme.colors.primary[700]
                    : (item.highlight ?? false)
                      ? currentTheme.colors.primary[700]
                      : currentTheme.colors.text.secondary,
                  border: (item.highlight ?? false)
                    ? `3px solid ${currentTheme.colors.primary[500]}`
                    : 'none',
                  boxShadow: (item.highlight ?? false)
                    ? `0 0 0 3px ${currentTheme.colors.primary[200]}`
                    : 'none'
                })}
              >
                <item.icon className={clsx(
                  "mr-3",
                  (item.highlight ?? false) ? "h-6 w-6" : "h-5 w-5"
                )} />
                {item.name}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className={clsx(
          "flex flex-1 flex-col border-r",
          currentTheme.styles.sidebar.background,
          currentTheme.styles.sidebar.border
        )}>
          <div className={clsx(
            "flex items-center h-16 px-6 border-b",
            currentTheme.styles.sidebar.border
          )}>
            <img src={EngiSpiderIcon} alt="EngiSpider" className="h-8 w-8" />
            <span className="ml-2 text-xl font-bold" style={{ color: currentTheme.colors.text.primary }}>Restro360</span>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-2">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center px-3 py-2 rounded-lg transition-all',
                    (item.highlight ?? false)
                      ? 'text-base font-bold shadow-lg'
                      : 'text-sm font-medium',
                    isActive
                      ? `text-primary-700`
                      : 'hover:bg-opacity-10'
                  )
                }
                style={({ isActive }) => ({
                  backgroundColor: isActive
                    ? currentTheme.colors.primary[100]
                    : (item.highlight ?? false)
                      ? currentTheme.colors.primary[100]
                      : 'transparent',
                  color: isActive
                    ? currentTheme.colors.primary[700]
                    : (item.highlight ?? false)
                      ? currentTheme.colors.primary[700]
                      : currentTheme.colors.text.secondary,
                  border: (item.highlight ?? false)
                    ? 'none'
                    : 'none',
                  boxShadow: (item.highlight ?? false)
                    ? `0 0 0 3px ${currentTheme.colors.primary[200]}`
                    : 'none'
                })}
              >
                <item.icon className={clsx(
                  "mr-3",
                  (item.highlight ?? false) ? "h-6 w-6" : "h-5 w-5"
                )} />
                {item.name}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className={clsx(
          "sticky top-0 z-40 border-b",
          currentTheme.styles.header.background,
          currentTheme.styles.header.border
        )}>
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-400 hover:text-gray-600"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            <div className="flex items-center space-x-4 flex-1 justify-end">
              <div className="hidden sm:block absolute left-1/2 transform -translate-x-1/2 lg:relative lg:left-auto lg:transform-none lg:mr-auto">
                <div className="px-4">
                  <span className="text-lg font-bold tracking-wide" 
                        style={{ 
                          color: currentTheme.colors.primary[700],
                          textShadow: `0 1px 2px ${currentTheme.colors.primary[100]}`
                        }}>
                    {user?.organizationName || user?.organizationCode}
                  </span>
                </div>
              </div>
              
              {/* Notification Bell */}
              {activeRestaurantId && (
                <div className="relative" ref={notificationRef}>
                  <button
                    onClick={() => setShowNotifications(s => !s)}
                    className="relative p-2 rounded-lg transition-colors hover:bg-gray-100"
                    title="Notifications"
                  >
                    {totalNotifications > 0 ? (
                      <BellRing className="h-5 w-5 text-red-500" />
                    ) : (
                      <Bell className="h-5 w-5" style={{ color: currentTheme.colors.text.secondary }} />
                    )}
                    {totalNotifications > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-bold leading-none">
                        {totalNotifications > 99 ? '99+' : totalNotifications}
                      </span>
                    )}
                  </button>

                  {showNotifications && (
                    <div className={clsx(
                      'absolute right-0 mt-2 w-96 rounded-lg shadow-xl border z-50 overflow-hidden',
                      currentTheme.styles.card.background,
                      currentTheme.styles.card.border
                    )}>
                      <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: currentTheme.colors.border.primary }}>
                        <h3 className="font-semibold text-sm" style={{ color: currentTheme.colors.text.primary }}>
                          Notifications
                        </h3>
                        {totalNotifications > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-600 font-medium">
                            {totalNotifications} pending
                          </span>
                        )}
                      </div>

                      <div className="max-h-[70vh] overflow-y-auto">
                        {/* Waiter Calls */}
                        {waiterCalls.length > 0 && (
                          <div className="p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: currentTheme.colors.text.secondary }}>
                              Waiter Calls
                            </p>
                            {waiterCalls.map(call => (
                              <div key={call.id} className="mb-2 p-3 rounded-lg border border-amber-400" style={{ backgroundColor: '#FEF3C7' }}>
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-semibold text-amber-800">🔔 Table: {call.tableName}</p>
                                    <p className="text-xs text-amber-600 mt-0.5">Customer is calling for a waiter</p>
                                  </div>
                                  <button
                                    onClick={() => handleAcknowledgeCall(call.id)}
                                    className="ml-3 flex-shrink-0 px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs rounded-lg font-medium transition-colors"
                                  >
                                    OK
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Order Requests */}
                        {pendingRequests.length > 0 && (
                          <div className="p-3">
                            <p className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: currentTheme.colors.text.secondary }}>
                              Order Requests
                            </p>
                            {pendingRequests.map(req => (
                              <div
                                key={req.id}
                                className="mb-3 p-3 rounded-lg border border-blue-400 cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                                style={{ backgroundColor: '#EFF6FF' }}
                                onClick={() => { navigate('/notifications'); setShowNotifications(false); }}
                              >
                                <div className="mb-2">
                                  <p className="text-sm font-semibold text-blue-800">🪑 {req.tableName || req.tableId}</p>
                                  <p className="text-xs text-blue-600 mt-0.5">{req.customerName} · {req.customerMobile}</p>
                                </div>
                                <div className="space-y-1 mb-2">
                                  {req.items.slice(0, 2).map((item, idx) => (
                                    <div key={idx} className="flex justify-between text-xs text-blue-700">
                                      <span>{item.itemName} × {item.quantity}</span>
                                      <span>₹{(item.unitPrice * item.quantity).toFixed(2)}</span>
                                    </div>
                                  ))}
                                  {req.items.length > 2 && (
                                    <p className="text-xs text-blue-500">+{req.items.length - 2} more items</p>
                                  )}
                                </div>
                                <div className="flex space-x-2" onClick={e => e.stopPropagation()}>
                                  <button
                                    onClick={() => handleAcceptRequest(req.id)}
                                    className="flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs rounded-lg font-medium transition-colors"
                                  >
                                    <CheckCircle className="h-3 w-3" />
                                    <span>Accept</span>
                                  </button>
                                  <button
                                    onClick={() => handleRejectRequest(req.id)}
                                    className="flex-1 flex items-center justify-center space-x-1 px-2 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs rounded-lg font-medium transition-colors"
                                  >
                                    <XCircle className="h-3 w-3" />
                                    <span>Reject</span>
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Empty state */}
                        {totalNotifications === 0 && (
                          <div className="p-6 text-center">
                            <Bell className="h-8 w-8 mx-auto mb-2" style={{ color: currentTheme.colors.text.secondary }} />
                            <p className="text-sm" style={{ color: currentTheme.colors.text.secondary }}>No pending notifications</p>
                          </div>
                        )}
                      </div>

                      {/* Footer — link to full page */}
                      <button
                        onClick={() => { navigate('/notifications'); setShowNotifications(false); }}
                        className="w-full flex items-center justify-center gap-1.5 px-4 py-2.5 text-xs font-medium border-t transition-colors hover:bg-opacity-10"
                        style={{
                          borderColor: currentTheme.colors.border.primary,
                          color: currentTheme.colors.primary[600],
                        }}
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                        View all notifications &amp; history
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Profile Dropdown */}
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className="flex items-center space-x-3 px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="text-right text-sm min-w-0">
                    <p className="font-medium truncate" style={{ color: currentTheme.colors.text.primary }}>
                      {user?.name || `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                    </p>
                    <p className="text-xs truncate" style={{ color: currentTheme.colors.text.secondary }}>{user?.email}</p>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center overflow-hidden" style={{ backgroundColor: currentTheme.colors.primary[100] }}>
                      {user?.photo ? (
                        <img src={user.photo} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <User className="h-4 w-4" style={{ color: currentTheme.colors.primary[600] }} />
                      )}
                    </div>
                    <ChevronDown className={clsx(
                      "h-4 w-4 transition-transform",
                      profileDropdownOpen && "rotate-180"
                    )} style={{ color: currentTheme.colors.text.secondary }} />
                  </div>
                </button>

                {/* Dropdown Menu */}
                {profileDropdownOpen && (
                  <div className={clsx(
                    "absolute right-0 mt-2 w-64 rounded-lg shadow-lg border py-1 z-50",
                    currentTheme.styles.card.background,
                    currentTheme.styles.card.border
                  )}>
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-opacity-10 flex items-center space-x-2"
                      style={{ color: currentTheme.colors.text.primary }}
                    >
                      <User className="h-4 w-4" />
                      <span>Profile</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/settings');
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm hover:bg-opacity-10 flex items-center space-x-2"
                      style={{ color: currentTheme.colors.text.primary }}
                    >
                      <Settings className="h-4 w-4" />
                      <span>Settings</span>
                    </button>
                    <hr className="my-1" style={{ borderColor: currentTheme.colors.border.primary }} />

                    {/* Restaurant selector (employees only) */}
                    {restaurants.length > 0 && (
                      <div className="px-4 py-2">
                        <button
                          onClick={() => setRestaurantSelectorOpen(!restaurantSelectorOpen)}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                          style={{ color: currentTheme.colors.text.primary }}
                        >
                          <div className="flex items-center space-x-2">
                            <Store className="h-4 w-4" style={{ color: currentTheme.colors.text.secondary }} />
                            <span className="font-medium">Restaurant</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs truncate max-w-[80px]" style={{ color: currentTheme.colors.text.secondary }}>
                              {restaurants.find(r => r.id === activeRestaurantId)?.name ?? ''}
                            </span>
                            <ChevronDown
                              className={clsx('h-4 w-4 transition-transform', restaurantSelectorOpen && 'rotate-180')}
                              style={{ color: currentTheme.colors.text.secondary }}
                            />
                          </div>
                        </button>
                        {restaurantSelectorOpen && (
                          <div className="mt-2 space-y-1 pl-6">
                            {restaurants.map(r => (
                              <button
                                key={r.id}
                                onClick={() => handleRestaurantChange(r.id)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                                style={{
                                  backgroundColor: activeRestaurantId === r.id ? currentTheme.colors.primary[100] : 'transparent',
                                  color: activeRestaurantId === r.id ? currentTheme.colors.primary[700] : currentTheme.colors.text.secondary,
                                }}
                              >
                                <div className="flex items-center space-x-3">
                                  <Store className="h-3 w-3" style={{ color: activeRestaurantId === r.id ? currentTheme.colors.primary[600] : currentTheme.colors.text.secondary }} />
                                  <span>{r.name}</span>
                                </div>
                                {activeRestaurantId === r.id && (
                                  <Check className="h-3 w-3" style={{ color: currentTheme.colors.primary[700] }} />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Role selector (shown when restaurant has multiple roles) */}
                    {activeRestaurantRoles.length > 1 && (
                      <div className="px-4 py-2">
                        <button
                          onClick={() => setRoleSelectorOpen(!roleSelectorOpen)}
                          className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                          style={{ color: currentTheme.colors.text.primary }}
                        >
                          <div className="flex items-center space-x-2">
                            <Shield className="h-4 w-4" style={{ color: currentTheme.colors.text.secondary }} />
                            <span className="font-medium">Role</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-xs truncate max-w-[80px]" style={{ color: currentTheme.colors.text.secondary }}>
                              {activeRole ?? 'All'}
                            </span>
                            <ChevronDown
                              className={clsx('h-4 w-4 transition-transform', roleSelectorOpen && 'rotate-180')}
                              style={{ color: currentTheme.colors.text.secondary }}
                            />
                          </div>
                        </button>
                        {roleSelectorOpen && (
                          <div className="mt-2 space-y-1 pl-6">
                            {activeRestaurantRoles.map(role => (
                              <button
                                key={role}
                                onClick={() => handleRoleChange(role)}
                                className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors hover:bg-opacity-10"
                                style={{
                                  backgroundColor: activeRole === role ? currentTheme.colors.primary[100] : 'transparent',
                                  color: activeRole === role ? currentTheme.colors.primary[700] : currentTheme.colors.text.secondary,
                                }}
                              >
                                <span>{role}</span>
                                {activeRole === role && (
                                  <Check className="h-3 w-3" style={{ color: currentTheme.colors.primary[700] }} />
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    <ThemeSelector />
                    <hr className="my-1" style={{ borderColor: currentTheme.colors.border.primary }} />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <Outlet />
        </main>
      </div>

      {/* Toast notification */}
      {toastMessage && (
        <div className="fixed top-4 right-4 z-[100] flex items-center space-x-2 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg">
          <BellRing className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm font-medium">{toastMessage}</span>
        </div>
      )}

      {/* Notification Popup Modal */}
      {currentNotif && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-6" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}>
          <div
            className="w-full max-w-sm rounded-2xl shadow-2xl p-6"
            style={{ backgroundColor: currentTheme.colors.background.card }}
          >
            {/* Badge */}
            <div
              className="inline-flex items-center px-3 py-1.5 rounded-full mb-4 text-sm font-bold"
              style={{
                backgroundColor: currentNotif.type === 'order' ? currentTheme.colors.primary[100] : '#FEF3C7',
                color: currentNotif.type === 'order' ? currentTheme.colors.primary[700] : '#92400E',
              }}
            >
              {currentNotif.type === 'order' ? '🛒 New Order Request' : '🔔 Waiter Called'}
            </div>

            {currentNotif.type === 'order' ? (
              <>
                <p className="text-lg font-bold mb-1" style={{ color: currentTheme.colors.text.primary }}>
                  {currentNotif.data.tableName || 'Table'} · {currentNotif.data.customerName}
                </p>
                {currentNotif.data.customerMobile && (
                  <p className="text-sm mb-3" style={{ color: currentTheme.colors.text.secondary }}>
                    {currentNotif.data.customerMobile}
                  </p>
                )}
                <hr className="mb-3" style={{ borderColor: currentTheme.colors.border.secondary }} />
                <div className="space-y-2 mb-3">
                  {currentNotif.data.items.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span style={{ color: currentTheme.colors.text.primary }}>{item.quantity}× {item.itemName}</span>
                      <span style={{ color: currentTheme.colors.text.secondary }}>₹{(item.quantity * item.unitPrice).toFixed(0)}</span>
                    </div>
                  ))}
                </div>
                {currentNotif.data.items.length > 0 && (
                  <div className="flex justify-between text-sm font-semibold mb-4">
                    <span style={{ color: currentTheme.colors.text.secondary }}>Total</span>
                    <span style={{ color: currentTheme.colors.text.primary }}>
                      ₹{currentNotif.data.items.reduce((s, i) => s + i.quantity * i.unitPrice, 0).toFixed(0)}
                    </span>
                  </div>
                )}
                <hr className="mb-4" style={{ borderColor: currentTheme.colors.border.secondary }} />
                <div className="flex gap-3">
                  <button
                    onClick={handlePopupDecline}
                    className="flex-1 py-3 rounded-xl text-sm font-bold transition-colors"
                    style={{ backgroundColor: '#FEE2E2', color: '#DC2626' }}
                  >
                    Decline
                  </button>
                  <button
                    onClick={handlePopupAccept}
                    className="flex-1 py-3 rounded-xl text-sm font-bold text-white transition-colors"
                    style={{ backgroundColor: currentTheme.colors.primary[600] }}
                  >
                    Accept
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-lg font-bold mb-1" style={{ color: currentTheme.colors.text.primary }}>
                  {currentNotif.data.tableName}
                </p>
                <p className="text-sm mb-5" style={{ color: currentTheme.colors.text.secondary }}>
                  This table is requesting assistance
                </p>
                <button
                  onClick={handlePopupAcknowledge}
                  className="w-full py-3 rounded-xl text-sm font-bold text-white transition-colors"
                  style={{ backgroundColor: currentTheme.colors.primary[600] }}
                >
                  Acknowledge
                </button>
              </>
            )}
          </div>
        </div>
      )}

    </div>
  );
};